// pms-adapters / supabase / vegaSeed — « Relecture Vega »: the seed's history and
// the operator's manual run (VEGA-SEED-BRIEF §12.1; migration 20261021200100).
//
// Two RPCs, both operator-only (`pms.seed.manage`):
//
//   · `pms_seed_overview`    — the pending request, the last request, the last pass
//     of any kind, and the last runs that did something with their findings.
//   · `pms_seed_request_run` — records a REQUEST for a run. It does not run the
//     seed: a seed takes 7–9 s on production and a signed-in call is cut at 8 s,
//     so a job drains the request within the minute and the run it became shows
//     in the overview.
//
// A missing capability is `forbidden`, never an empty history: « nothing ran »
// and « you may not look » must not read the same.
//
// This adapter is deliberately separate from `vegaMirror.ts`, whose port is
// read-only by construction. A relecture writes `pms_*` — never Vega — and the
// read-only guarantee of the mirror must not be diluted by sharing a class with
// something that writes.

import {
  err,
  ok,
  type IsoDate,
  type PropertyCode,
  type Result,
  type SeedAttention,
  type SeedLastCheck,
  type SeedLastRequest,
  type SeedOverview,
  type SeedPendingRequest,
  type SeedRun,
  type SeedRunStatus,
  type SeedTrigger,
  type VegaSeed,
} from '@lemontaigu/pms-core';
import type { PostgrestError } from '@supabase/supabase-js';
import { toPmsError, type SupabaseClient } from './support.js';

/** The RPC's jsonb, verbatim (already camelCase: built by the function). */
export interface SeedOverviewJson {
  property: string;
  pending: { id: number; requestedAt: string } | null;
  lastRequest: {
    id: number;
    status: string;
    requestedAt: string;
    finishedAt: string | null;
    runId: number | null;
    error: string | null;
  } | null;
  lastCheck: {
    id: number;
    importId: number | null;
    status: string;
    trigger: string;
    startedAt: string;
    reason: string | null;
  } | null;
  runs: Array<{
    id: number;
    importId: number | null;
    trigger: string;
    status: string;
    startedAt: string;
    finishedAt: string | null;
    windowFrom: string | null;
    bookings: Record<string, unknown> | null;
    stays: Record<string, unknown> | null;
    charges: Record<string, unknown> | null;
    attentionTotal: number | null;
    attention: Array<{ kind?: unknown; resa?: unknown; detail?: unknown }> | null;
  }>;
}

const TRIGGERS: readonly SeedTrigger[] = ['cron', 'manual', 'backfill'];
const STATUSES: readonly SeedRunStatus[] = ['applied', 'noop', 'failed'];

/** A count the function may not have (an early pass has no `charges`): null, never 0. */
const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);
const str = (v: unknown): string | null => (typeof v === 'string' ? v : null);

function toAttention(items: SeedOverviewJson['runs'][number]['attention']): SeedAttention[] {
  return (items ?? []).map((a) => ({
    // A finding without a kind is still a finding: shown, not dropped.
    kind: str(a?.kind) ?? '(sans type)',
    resa: str(a?.resa),
    detail: str(a?.detail),
  }));
}

/** Maps the reply. Returns null when it is not a reply this build can read —
 *  including a trigger or a status it does not know, which must not show as a
 *  pass that « did something ordinary ». */
export function toSeedOverview(j: SeedOverviewJson | null): SeedOverview | null {
  if (!j || typeof j !== 'object' || typeof j.property !== 'string' || !Array.isArray(j.runs)) return null;

  const runs: SeedRun[] = [];
  for (const r of j.runs) {
    const trigger = TRIGGERS.find((t) => t === r.trigger);
    const status = STATUSES.find((s) => s === r.status);
    if (!trigger || !status || typeof r.id !== 'number') return null;
    runs.push({
      id: r.id,
      importId: num(r.importId),
      trigger,
      status,
      startedAt: r.startedAt,
      finishedAt: r.finishedAt ?? null,
      windowFrom: (r.windowFrom as IsoDate | null) ?? null,
      bookings: {
        seen: num(r.bookings?.seen),
        inserted: num(r.bookings?.inserted),
        updated: num(r.bookings?.updated),
        cancelled: num(r.bookings?.cancelled),
        refused: num(r.bookings?.refused),
      },
      stays: {
        inserted: num(r.stays?.inserted),
        updated: num(r.stays?.updated),
        released: num(r.stays?.released),
      },
      charges: r.charges
        ? {
            withPlan: num(r.charges.withPlan),
            planSplit: num(r.charges.planSplit),
            inserted: num(r.charges.inserted),
            voided: num(r.charges.voided),
            refused: num(r.charges.refused),
            state: str(r.charges.state),
          }
        : null,
      attentionTotal: num(r.attentionTotal) ?? 0,
      attention: toAttention(r.attention),
    });
  }

  let lastCheck: SeedLastCheck | null = null;
  if (j.lastCheck) {
    const trigger = TRIGGERS.find((t) => t === j.lastCheck!.trigger);
    const status = STATUSES.find((s) => s === j.lastCheck!.status);
    if (!trigger || !status) return null;
    lastCheck = {
      id: j.lastCheck.id,
      importId: num(j.lastCheck.importId),
      status,
      trigger,
      startedAt: j.lastCheck.startedAt,
      reason: str(j.lastCheck.reason),
    };
  }

  let lastRequest: SeedLastRequest | null = null;
  if (j.lastRequest) {
    const status = j.lastRequest.status === 'done' || j.lastRequest.status === 'failed' ? j.lastRequest.status : null;
    if (!status) return null;
    lastRequest = {
      id: j.lastRequest.id,
      status,
      requestedAt: j.lastRequest.requestedAt,
      finishedAt: j.lastRequest.finishedAt ?? null,
      runId: num(j.lastRequest.runId),
      error: str(j.lastRequest.error),
    };
  }

  const pending: SeedPendingRequest | null = j.pending
    ? { id: j.pending.id, requestedAt: j.pending.requestedAt }
    : null;

  return { property: j.property as PropertyCode, pending, lastRequest, lastCheck, runs };
}

/** The function's own refusals, without its name in front. */
function refusal(e: PostgrestError, what: string): Result<never> {
  if (e.code === '42501') return err('forbidden', `Accès refusé — ${what}`, { cause: e });
  if (e.code === 'P0001') {
    return err('validation', e.message.replace(/^pms_seed_\w+:\s*/, ''), { cause: e });
  }
  return { ok: false, error: toPmsError(e) };
}

export class SupabaseVegaSeed implements VegaSeed {
  constructor(private readonly db: SupabaseClient) {}

  async overview(propertyCode: PropertyCode, limit = 15): Promise<Result<SeedOverview>> {
    const r = await this.db.rpc('pms_seed_overview', { p_property: propertyCode, p_limit: limit });
    if (r.error) return refusal(r.error, 'relecture Vega');
    const overview = toSeedOverview(r.data as SeedOverviewJson | null);
    // An older build than the database: refuse rather than show less.
    if (!overview) return err('unknown', 'Relecture Vega : réponse illisible — recharger la page', { retryable: false });
    return ok(overview);
  }

  async requestRun(propertyCode: PropertyCode): Promise<Result<number>> {
    const r = await this.db.rpc('pms_seed_request_run', { p_property: propertyCode });
    if (r.error) return refusal(r.error, 'demande de relecture');
    if (typeof r.data !== 'number') {
      return err('unknown', 'Demande de relecture : réponse illisible', { retryable: false });
    }
    return ok(r.data);
  }
}
